import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { rccColors } from "./colors";
import { useRccTypography } from "./typography";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    padding: "4px 8px", // Slightly tighter padding for badges
    gap: "8px",
    minWidth: "fit-content",
    // Dynamic background based on props
    backgroundColor: ({ variant }) =>
      variant === "future" ? rccColors.blue[50] : rccColors.green[50],
  },
  icon: {
    color: ({ variant }) =>
      variant === "future" ? rccColors.lightBlue[900] : rccColors.green[900],
    fontSize: "14px", // Matches text size visually
    display: "block",
  },
  text: {
    color: ({ variant }) =>
      variant === "future" ? rccColors.lightBlue[900] : rccColors.green[900],
    whiteSpace: "nowrap",
    lineHeight: 1, // Ensures vertical alignment
  },
}));

/**
 * A reusable badge component with two variants:
 * 1. "current" (default): Green bg, Today icon, "Current Qtr Focus"
 * 2. "future": Blue bg, Timeline icon, "Future Projection"
 */
function FocusPeriodCard({ variant = "current", label }) {
  const classes = useStyles({ variant });
  const typography = useRccTypography();

  // Configuration map for the two states
  const config = {
    current: {
      defaultLabel: "Current Qtr Trend",
      icon: "today", // Material Icon name
    },
    future: {
      defaultLabel: "Leading Indicator",
      icon: "timeline", // Material Icon name
    },
  };

  // Get settings based on variant, fallback to current if invalid
  const activeConfig = config[variant] || config.current;
  const textLabel = label || activeConfig.defaultLabel;

  return (
    <Box className={classes.root}>
      {/* Icon */}
      <span className={`material-icons ${classes.icon}`}>
        {activeConfig.icon}
      </span>

      {/* Label Text */}
      <Typography
        component="span"
        className={`${typography.textExtraSmallMedium} ${classes.text}`}
      >
        {textLabel}
      </Typography>
    </Box>
  );
}

FocusPeriodCard.propTypes = {
  /** 'current' (Green) or 'future' (Blue) */
  variant: PropTypes.oneOf(["current", "future"]),
  /** Optional override for the text label */
  label: PropTypes.string,
};

export default FocusPeriodCard;
